import classNames from "classnames";
import { throttle } from "lodash";
import React from "react";
import { useResizeDetector } from "react-resize-detector";

interface Props {
  className?: string;
  isAutoHeight?: boolean;
  isDisabled?: boolean;
  style?: React.CSSProperties;

  children: any;
}

const FullHeightContainer = (props: Props) => {
  const [height, setHeight] = React.useState<number | undefined>();

  const ref = React.useRef<HTMLDivElement | null>(null);

  const adjustHeight = React.useCallback(() => {
    const target = ref.current;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    let height = window.innerHeight - rect.top;

    // subtruct all parents bottom padding and border
    let parent = target.parentElement;
    while (parent && !parent.classList.contains("main_content")) {
      const cs = window.getComputedStyle(parent);
      height -= parseInt(cs.paddingBottom) + parseInt(cs.borderBottom);
      parent = parent.parentElement;
    }
    setHeight(height);
  }, []);

  React.useEffect(() => {
    const throttled = throttle(adjustHeight, 100);

    window.addEventListener("resize", throttled);

    window.requestAnimationFrame(adjustHeight); // firefox fix

    return () => {
      window.removeEventListener("resize", throttled);
    };
  }, [adjustHeight]);

  const style = React.useMemo(
    (): React.CSSProperties | undefined =>
      props.isDisabled
        ? props.style
        : {
            ...props.style,
            height: props.isAutoHeight ? undefined : height,
            maxHeight: props.isAutoHeight ? height : undefined
          },
    [height, props.isDisabled, props.style]
  );

  useResizeDetector({
    handleHeight: true,
    onResize: adjustHeight,
    refreshMode: "throttle",
    refreshRate: 100,
    targetRef: ref
  });

  return (
    <div
      ref={ref}
      className={classNames("full_height_container", props.className, {
        disabled: props.isDisabled
      })}
      style={style}
    >
      {props.children}
    </div>
  );
};

export default FullHeightContainer;
